import aiAgentsService from "../../services/aiAgentService";
import { devLog } from "../../utils/devLog";

const TOKEN_REFRESH_INTERVAL_MS = 8 * 60 * 1000; // Azure tokens expire after 10 min

export const SPEAKER_STAFF = "Staff";
export const SPEAKER_PATIENT = "Patient";

/**
 * Live speaker-labelled transcription of an in-app video call.
 *
 * Runs one Azure Speech recognizer on the staff microphone and one on the
 * patient's remote audio track, so every recognised utterance is attributed
 * without diarization. No audio is recorded or stored — only text leaves the
 * browser. If the facility has no AZURE_SPEECH integration the token request
 * fails and start() rejects, which callers treat as "transcription off".
 *
 * Uses window.SpeechSDK (CDN bundle in index.html, same as TipTap dictation
 * and the ambient note recorder) and the shared /api/artifact/speechToken
 * endpoint.
 */
export default class CallTranscription {
  constructor(labels = {}) {
    this.entries = [];
    this.recognizers = {};
    this.active = false;
    this.pendingRemoteTrack = null;
    this.refreshTimer = null;
    this.staffLabel = labels.staffLabel || SPEAKER_STAFF;
    this.patientLabel = labels.patientLabel || SPEAKER_PATIENT;
  }

  async start() {
    if (!window.SpeechSDK) {
      throw new Error("SpeechSDK is not loaded");
    }
    const { token, region } = await aiAgentsService.getSpeechToken();
    if (!token || !region) {
      throw new Error("No speech token available");
    }
    this.token = token;
    this.region = region;
    this.active = true;
    this.startRecognizer(
      this.staffLabel,
      window.SpeechSDK.AudioConfig.fromDefaultMicrophoneInput()
    );
    if (this.pendingRemoteTrack) {
      this.addRemoteAudioTrack(this.pendingRemoteTrack);
      this.pendingRemoteTrack = null;
    }
    this.refreshTimer = setInterval(
      this.refreshToken,
      TOKEN_REFRESH_INTERVAL_MS
    );
  }

  addRemoteAudioTrack(mediaStreamTrack) {
    if (!this.active) {
      this.pendingRemoteTrack = mediaStreamTrack;
      return;
    }
    this.stopRecognizer(this.patientLabel);
    const stream = new MediaStream([mediaStreamTrack]);
    this.startRecognizer(
      this.patientLabel,
      window.SpeechSDK.AudioConfig.fromStreamInput(stream)
    );
  }

  startRecognizer(speaker, audioConfig) {
    try {
      const SpeechSDK = window.SpeechSDK;
      const speechConfig = SpeechSDK.SpeechConfig.fromAuthorizationToken(
        this.token,
        this.region
      );
      const recognizer = new SpeechSDK.SpeechRecognizer(
        speechConfig,
        audioConfig
      );
      recognizer.recognized = (sender, event) => {
        if (
          event.result &&
          event.result.reason === SpeechSDK.ResultReason.RecognizedSpeech &&
          event.result.text
        ) {
          this.entries.push({
            speaker,
            text: event.result.text,
            at: Date.now(),
          });
        }
      };
      recognizer.startContinuousRecognitionAsync(
        () => devLog(`[CALL TRANSCRIPTION] ${speaker} recognizer started`),
        (err) =>
          devLog(`[CALL TRANSCRIPTION] ${speaker} recognizer failed`, err)
      );
      this.recognizers[speaker] = recognizer;
    } catch (err) {
      devLog(`[CALL TRANSCRIPTION] could not start ${speaker} recognizer`, err);
    }
  }

  stopRecognizer(speaker) {
    const recognizer = this.recognizers[speaker];
    if (!recognizer) return;
    delete this.recognizers[speaker];
    try {
      recognizer.stopContinuousRecognitionAsync(
        () => recognizer.close(),
        () => recognizer.close()
      );
    } catch (err) {
      devLog(`[CALL TRANSCRIPTION] error stopping ${speaker} recognizer`, err);
    }
  }

  refreshToken = async () => {
    try {
      const { token } = await aiAgentsService.getSpeechToken();
      this.token = token;
      Object.values(this.recognizers).forEach((recognizer) => {
        recognizer.authorizationToken = token;
      });
    } catch (err) {
      devLog("[CALL TRANSCRIPTION] token refresh failed", err);
    }
  };

  stop() {
    this.active = false;
    this.pendingRemoteTrack = null;
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    Object.keys(this.recognizers).forEach((speaker) =>
      this.stopRecognizer(speaker)
    );
  }

  getTranscript() {
    return this.entries
      .slice()
      .sort((a, b) => a.at - b.at)
      .map((entry) => `${entry.speaker}: ${entry.text}`)
      .join("\n");
  }
}
